import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, isToday, isThisWeek, isThisMonth } from 'date-fns';

import { listSchedules, cancelSchedule, linkSession, bulkCancelSchedules, exportSchedulesCsv } from '../api/schedules';
import { createSession } from '../api/sessions';
import { listAllOrganizations } from '../api/orgs';
import { authStore } from '../api/client';
import { formatDateTime, statusColor } from '../utils/format';

import EmptyState   from '../components/ui/EmptyState';
import ScheduleForm from '../components/interviews/ScheduleForm';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar-overrides.css';

// ---------------------------------------------------------------------------
// Calendar localizer
// ---------------------------------------------------------------------------

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {},
});

// ---------------------------------------------------------------------------
// Status -> color (used by both calendar event painter and table badges)
// ---------------------------------------------------------------------------

const STATUS_COLORS = {
  SCHEDULED: { bg: '#0d9488', border: '#0f766e' }, // teal-600 / teal-700
  ACTIVE:    { bg: '#16a34a', border: '#15803d' },
  COMPLETED: { bg: '#64748b', border: '#475569' },
  CANCELLED: { bg: '#dc2626', border: '#b91c1c' },
};

const DEFAULT_COLOR = { bg: '#94a3b8', border: '#64748b' };

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Badge({ label, colorClass }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${colorClass}`}>
      {label}
    </span>
  );
}

function Toast({ message, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-50 bg-green-600 text-white text-sm
                 font-medium px-5 py-3 rounded-xl shadow-xl flex items-center gap-2"
    >
      <span aria-hidden="true">✓</span>
      {message}
    </div>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      console.log('[InterviewsPage] copied invite link');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('[InterviewsPage] clipboard write failed');
    }
  }, [value]);

  if (!value) return <span className="text-slate-400 italic text-xs">No link</span>;

  return (
    <button
      onClick={handleCopy}
      title={value}
      className="px-3 py-1.5 text-xs font-medium rounded border border-slate-300
                 text-slate-700 hover:bg-slate-100 transition-colors"
    >
      {copied ? 'Copied!' : 'Copy invite link'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/**
 * Map a ScheduleResponse into a react-big-calendar event.
 */
function scheduleToEvent(schedule) {
  const start = new Date(schedule.scheduledAt);
  const end   = new Date(start.getTime() + (schedule.durationMinutes || 60) * 60000);
  return {
    id:    schedule.id,
    title: `${schedule.title || 'Interview'} - ${schedule.candidateName || schedule.candidateEmail || 'Candidate'}`,
    start,
    end,
    resource: schedule,
  };
}

/**
 * Apply Status + Date Range filters to the schedules list.
 */
function applyFilters(schedules, statusFilter, dateFilter) {
  return schedules.filter((s) => {
    // Status filter
    if (statusFilter !== 'ALL' && (s.status || '').toUpperCase() !== statusFilter) {
      return false;
    }
    // Date filter
    if (dateFilter !== 'ALL') {
      const d = new Date(s.scheduledAt);
      if (dateFilter === 'TODAY'      && !isToday(d))                         return false;
      if (dateFilter === 'THIS_WEEK'  && !isThisWeek(d, { weekStartsOn: 0 })) return false;
      if (dateFilter === 'THIS_MONTH' && !isThisMonth(d))                     return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// InterviewsPage
// ---------------------------------------------------------------------------

/**
 * InterviewsPage
 *
 * Route: /interviews
 *
 * Two views, toggled from the toolbar:
 *   - Calendar (react-big-calendar) - month/week/day, color-coded by status
 *   - List     (sortable table)     - status + date range filters
 *
 * Clicking any event/row opens a side panel with full details and
 * context-aware actions (Start / Rejoin / Cancel / View Report).
 */
export default function InterviewsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role   = authStore.role;
  const userEmail = authStore.user?.email || '';

  // ── Data ───────────────────────────────────────────────────────────────
  const [schedules,   setSchedules]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [orgList,     setOrgList]     = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(() => searchParams.get('orgId') || '');

  // ── UI state ───────────────────────────────────────────────────────────
  const [view,                setView]                = useState('calendar'); // 'calendar' | 'list'
  const [showForm,            setShowForm]            = useState(false);
  const [selectedInterview,   setSelectedInterview]   = useState(null);
  const [actionLoading,       setActionLoading]       = useState(false);
  const [toast,               setToast]               = useState(null);

  // ── Filters (list view) ────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFilter,   setDateFilter]   = useState('ALL');

  // -------------------------------------------------------------------------
  // Load
  // -------------------------------------------------------------------------

  useEffect(() => {
    const q = searchParams.get('orgId') || '';
    setSelectedOrgId(q);
  }, [searchParams]);

  useEffect(() => {
    if (role !== 'SUPER_ADMIN') return;
    listAllOrganizations()
      .then((res) => {
        const d = res.data;
        setOrgList(Array.isArray(d) ? d : (d?.content ?? d?.organizations ?? []));
      })
      .catch((err) => console.error('[InterviewsPage] listAllOrganizations failed:', err.message));
  }, [role]);

  const loadSchedules = useCallback(async () => {
    if (role !== 'SUPER_ADMIN' && !userEmail) {
      console.warn('[InterviewsPage] no authenticated user email - skipping load');
      return;
    }
    setLoading(true);
    setError('');

    try {
      let res;
      if (role === 'SUPER_ADMIN') {
        const params = selectedOrgId ? { orgId: selectedOrgId } : {};
        console.log('[InterviewsPage] loading schedules (platform)', params);
        res = await listSchedules(params);
      } else {
        console.log('[InterviewsPage] loading schedules for', userEmail);
        res = await listSchedules(userEmail);
      }
      let list = Array.isArray(res.data)
        ? res.data
        : (res.data?.content ?? res.data?.schedules ?? []);
      if (role === 'INTERVIEWER') {
        const em = userEmail.toLowerCase();
        list = list.filter(
          (s) => (s.createdBy || s.hostEmail || '').toLowerCase() === em,
        );
      }
      console.log('[InterviewsPage] loaded', list.length, 'schedules');
      setSchedules(list);
    } catch (err) {
      console.error('[InterviewsPage] listSchedules failed:', err.message);
      setError(err.message || 'Failed to load interviews.');
    } finally {
      setLoading(false);
    }
  }, [role, userEmail, selectedOrgId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const events = useMemo(
    () => schedules.map(scheduleToEvent),
    [schedules]
  );

  const filteredList = useMemo(() => {
    const list = applyFilters(schedules, statusFilter, dateFilter);
    return list.slice().sort((a, b) =>
      new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
    );
  }, [schedules, statusFilter, dateFilter]);

  // -------------------------------------------------------------------------
  // Calendar handlers
  // -------------------------------------------------------------------------

  const eventPropGetter = useCallback((event) => {
    const status = (event.resource?.status || '').toUpperCase();
    const color  = STATUS_COLORS[status] || DEFAULT_COLOR;
    return {
      style: {
        backgroundColor: color.bg,
        borderColor:     color.border,
        color:           '#ffffff',
      },
    };
  }, []);

  const handleSelectEvent = useCallback((event) => {
    console.log('[InterviewsPage] event selected', event.id);
    setSelectedInterview(event.resource);
  }, []);

  // -------------------------------------------------------------------------
  // Action handlers (Start / Rejoin / Cancel / View Report)
  // -------------------------------------------------------------------------

  const handleStart = useCallback(async (schedule) => {
    console.log('[InterviewsPage] starting interview for schedule', schedule.id);
    setActionLoading(true);
    try {
      const sessionRes = await createSession();
      const sessionId  = sessionRes?.data?.sessionId;
      if (!sessionId) throw new Error('Session response missing sessionId');

      console.log('[InterviewsPage] linking schedule', schedule.id, 'to session', sessionId);
      await linkSession(schedule.id, sessionId);

      setToast('Interview started');
      setSelectedInterview(null);
      navigate(`/session/${sessionId}`);
    } catch (err) {
      console.error('[InterviewsPage] handleStart failed:', err.message);
      setError(err.message || 'Failed to start interview.');
    } finally {
      setActionLoading(false);
    }
  }, [navigate]);

  const handleRejoin = useCallback((schedule) => {
    console.log('[InterviewsPage] rejoining session', schedule.sessionId);
    setSelectedInterview(null);
    navigate(`/session/${schedule.sessionId}`);
  }, [navigate]);

  const handleCancel = useCallback(async (schedule) => {
    const displayName = schedule.title || schedule.candidateEmail || `#${schedule.id}`;
    if (!window.confirm(`Cancel "${displayName}"? This cannot be undone.`)) return;

    console.log('[InterviewsPage] cancelling schedule', schedule.id);
    setActionLoading(true);
    try {
      await cancelSchedule(schedule.id);
      setToast('Interview cancelled');
      setSelectedInterview(null);
      await loadSchedules();
    } catch (err) {
      console.error('[InterviewsPage] cancelSchedule failed:', err.message);
      setError(err.message || 'Failed to cancel interview.');
    } finally {
      setActionLoading(false);
    }
  }, [loadSchedules]);

  const handleViewReport = useCallback((schedule) => {
    console.log('[InterviewsPage] viewing report for session', schedule.sessionId);
    setSelectedInterview(null);
    navigate(`/report/${schedule.sessionId}`);
  }, [navigate]);

  // -------------------------------------------------------------------------
  // ScheduleForm callbacks
  // -------------------------------------------------------------------------

  const handleFormSuccess = useCallback(async () => {
    console.log('[InterviewsPage] schedule created - refreshing list');
    setShowForm(false);
    setToast('Interview scheduled - invite sent to candidate email');
    await loadSchedules();
  }, [loadSchedules]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const hostEmail = authStore.user?.email    || '';
  const hostName  = authStore.user?.fullName || '';

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Scheduling</p>
        <h1 className="font-heading text-2xl font-bold text-ink tracking-tight mt-0.5">Interviews</h1>
        <p className="text-sm text-ink-faint mt-1">
          Calendar and list of scheduled sessions — start, rejoin, or cancel from here.
        </p>
      </div>

      {role === 'SUPER_ADMIN' && (
        <div className="max-w-md">
          <label htmlFor="interviews-org-filter" className="block text-xs font-medium text-slate-600 mb-1">
            Organization
          </label>
          <select
            id="interviews-org-filter"
            className="input w-full"
            value={selectedOrgId}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedOrgId(v);
              const next = new URLSearchParams(searchParams);
              if (v) next.set('orgId', v);
              else next.delete('orgId');
              setSearchParams(next);
            }}
          >
            <option value="">All orgs</option>
            {orgList.map((o) => {
              const id = o.id ?? o.orgId;
              return (
                <option key={id} value={id}>
                  {o.name || o.slug || id}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {/* View toggle */}
        <div className="inline-flex bg-line-soft/80 border border-line rounded-xl p-1 shadow-inner self-start">
          <button
            type="button"
            onClick={() => setView('calendar')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              view === 'calendar'
                ? 'bg-surface text-teal-700 shadow-xs'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            Calendar
          </button>
          <button
            type="button"
            onClick={() => setView('list')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              view === 'list'
                ? 'bg-surface text-teal-700 shadow-xs'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            List
          </button>
        </div>

        {/* Schedule button */}
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className={`btn btn-md ${showForm ? 'btn-secondary' : 'btn-primary'} self-start sm:self-auto`}
        >
          {showForm ? 'Cancel' : '+ Schedule Interview'}
        </button>
      </div>

      {/* ── Inline Schedule Form ─────────────────────────────────────────── */}
      {showForm && (
        <div className="mb-6">
          <ScheduleForm
            defaultHostEmail={hostEmail}
            defaultHostName={hostName}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200
                        text-red-700 text-sm rounded-lg" role="alert">
          {error}
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────────────── */}
      {view === 'calendar' ? (
        <CalendarView
          events={events}
          loading={loading}
          eventPropGetter={eventPropGetter}
          onSelectEvent={handleSelectEvent}
        />
      ) : (
        <ListView
          schedules={filteredList}
          loading={loading}
          statusFilter={statusFilter}
          dateFilter={dateFilter}
          onStatusFilterChange={setStatusFilter}
          onDateFilterChange={setDateFilter}
          onRowClick={setSelectedInterview}
          onStart={handleStart}
          onRejoin={handleRejoin}
          onCancel={handleCancel}
          onViewReport={handleViewReport}
          actionLoading={actionLoading}
          onRefresh={loadSchedules}
          onToast={setToast}
          onError={setError}
        />
      )}

      {/* ── Side detail panel ────────────────────────────────────────────── */}
      {selectedInterview && (
        <DetailPanel
          interview={selectedInterview}
          onClose={() => setSelectedInterview(null)}
          onStart={handleStart}
          onRejoin={handleRejoin}
          onCancel={handleCancel}
          onViewReport={handleViewReport}
          actionLoading={actionLoading}
        />
      )}

      {/* ── Toast ────────────────────────────────────────────────────────── */}
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CalendarView
// ---------------------------------------------------------------------------

function CalendarView({ events, loading, eventPropGetter, onSelectEvent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-soft p-4 sm:p-5">
      {loading && (
        <p className="text-xs text-slate-500 mb-2">Loading interviews...</p>
      )}
      <Calendar
        localizer={localizer}
        events={events}
        defaultView="week"
        views={['month', 'week', 'day']}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 'calc(100vh - 220px)' }}
        eventPropGetter={eventPropGetter}
        onSelectEvent={onSelectEvent}
        popup
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ListView
// ---------------------------------------------------------------------------

function ListView({
  schedules,
  loading,
  statusFilter,
  dateFilter,
  onStatusFilterChange,
  onDateFilterChange,
  onRowClick,
  onStart,
  onRejoin,
  onCancel,
  onViewReport,
  actionLoading,
  onRefresh,
  onToast,
  onError,
}) {
  // ── Selection state (bulk actions) ─────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy,    setBulkBusy]    = useState(false);

  // Drop selections that no longer exist in the visible page (e.g. after refresh).
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(schedules.map((s) => s.id));
      const next    = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [schedules]);

  const allChecked = schedules.length > 0 && schedules.every((s) => selectedIds.has(s.id));
  const someChecked = !allChecked && schedules.some((s) => selectedIds.has(s.id));

  const toggleAll = () => {
    setSelectedIds(allChecked ? new Set() : new Set(schedules.map((s) => s.id)));
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkCancel = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Cancel ${ids.length} interview${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;

    console.log('[InterviewsPage] bulk cancelling', ids.length, 'schedules');
    setBulkBusy(true);
    try {
      const res     = await bulkCancelSchedules(ids);
      const outcome = res?.data || {};
      const okCount = Object.values(outcome).filter((v) => v === 'ok').length;
      onToast?.(`Cancelled ${okCount} of ${ids.length} interviews`);
      setSelectedIds(new Set());
      await onRefresh?.();
    } catch (err) {
      console.error('[InterviewsPage] bulk cancel failed:', err.message);
      onError?.(err.message || 'Bulk cancel failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleExportCsv = async () => {
    console.log('[InterviewsPage] exporting CSV');
    setBulkBusy(true);
    try {
      const res  = await exportSchedulesCsv();
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'interviews.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onToast?.('Export started');
    } catch (err) {
      console.error('[InterviewsPage] export failed:', err.message);
      onError?.(err.message || 'Export failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ── Filters + bulk toolbar ───────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={onStatusFilterChange}
          options={[
            { value: 'ALL',       label: 'All statuses' },
            { value: 'SCHEDULED', label: 'Scheduled' },
            { value: 'ACTIVE',    label: 'Active' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'CANCELLED', label: 'Cancelled' },
          ]}
        />
        <FilterSelect
          label="Date"
          value={dateFilter}
          onChange={onDateFilterChange}
          options={[
            { value: 'ALL',        label: 'All dates' },
            { value: 'TODAY',      label: 'Today' },
            { value: 'THIS_WEEK',  label: 'This week' },
            { value: 'THIS_MONTH', label: 'This month' },
          ]}
        />
        <span className="text-xs text-slate-500 ml-auto">
          {schedules.length} interview{schedules.length === 1 ? '' : 's'}
        </span>

        {/* Bulk actions */}
        <button
          type="button"
          disabled={selectedIds.size === 0 || bulkBusy}
          onClick={handleBulkCancel}
          className="px-3 py-1.5 text-xs font-semibold rounded border border-red-300
                     text-red-700 bg-white hover:bg-red-50
                     disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Cancel selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
        </button>
        <button
          type="button"
          disabled={bulkBusy}
          onClick={handleExportCsv}
          className="px-3 py-1.5 text-xs font-semibold rounded border border-slate-300
                     text-slate-700 bg-white hover:bg-slate-50
                     disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Export CSV
        </button>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-2 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-slate-100 rounded" />
            ))}
          </div>
        ) : schedules.length === 0 ? (
          <EmptyState
            icon="📅"
            title="No interviews match your filters"
            subtitle="Try changing the status or date filter, or schedule a new interview."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={toggleAll}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Select all interviews on this page"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date &amp; Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {schedules.map((s) => {
                  const status = (s.status || '').toUpperCase();
                  return (
                    <tr
                      key={s.id}
                      onClick={() => onRowClick(s)}
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(s.id)}
                          onChange={() => toggleOne(s.id)}
                          aria-label={`Select interview ${s.title || s.candidateEmail || s.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-slate-800 max-w-[200px] truncate">
                        {s.title || <span className="text-slate-400 italic">Untitled</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-800">{s.candidateName || '-'}</div>
                        <div className="text-slate-500 text-xs">{s.candidateEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                        {formatDateTime(s.scheduledAt)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">
                        {s.durationMinutes != null ? `${s.durationMinutes}m` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge label={status || '-'} colorClass={statusColor(status)} />
                      </td>
                      <td
                        className="px-4 py-3 text-right whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowActions
                          schedule={s}
                          onStart={onStart}
                          onRejoin={onRejoin}
                          onCancel={onCancel}
                          onViewReport={onViewReport}
                          loading={actionLoading}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-500">
      <span className="font-medium uppercase tracking-wider">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5
                   text-sm text-slate-700 focus:outline-none focus:ring-2
                   focus:ring-teal-500/30 focus:border-teal-400"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// RowActions - compact context-aware buttons used in the list
// ---------------------------------------------------------------------------

function RowActions({ schedule, onStart, onRejoin, onCancel, onViewReport, loading }) {
  const status = (schedule.status || '').toUpperCase();
  const btn    = 'px-2.5 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  if (status === 'SCHEDULED' && !schedule.sessionId) {
    return (
      <div className="inline-flex items-center gap-2">
        <button
          disabled={loading}
          onClick={() => onStart(schedule)}
          className={`${btn} bg-green-600 hover:bg-green-500 text-white`}
        >
          Start
        </button>
        <button
          disabled={loading}
          onClick={() => onCancel(schedule)}
          className={`${btn} border border-red-300 text-red-600 hover:bg-red-50`}
        >
          Cancel
        </button>
      </div>
    );
  }
  if (status === 'ACTIVE' && schedule.sessionId) {
    return (
      <button
        disabled={loading}
        onClick={() => onRejoin(schedule)}
        className={`${btn} bg-green-600 hover:bg-green-500 text-white`}
      >
        Rejoin
      </button>
    );
  }
  if (status === 'COMPLETED' && schedule.sessionId) {
    return (
      <button
        disabled={loading}
        onClick={() => onViewReport(schedule)}
        className={`${btn} bg-slate-700 hover:bg-slate-600 text-white`}
      >
        View Report
      </button>
    );
  }
  return <span className="text-slate-400 text-xs italic">-</span>;
}

// ---------------------------------------------------------------------------
// DetailPanel - slide-in side panel for the selected interview
// ---------------------------------------------------------------------------

function DetailPanel({
  interview,
  onClose,
  onStart,
  onRejoin,
  onCancel,
  onViewReport,
  actionLoading,
}) {
  const status     = (interview.status || '').toUpperCase();
  const inviteLink = interview.inviteDeepLink ?? interview.inviteLink ?? interview.inviteUrl;

  return (
    <>
      {/* Click-away backdrop */}
      <div
        className="fixed inset-0 z-30 bg-slate-900/20"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        className="fixed top-0 right-0 z-40 h-full w-96 bg-white shadow-2xl
                   border-l border-slate-200 flex flex-col"
        role="dialog"
        aria-label="Interview details"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 leading-tight truncate">
              {interview.title || 'Untitled interview'}
            </h2>
            <div className="mt-2">
              <Badge label={status || '-'} colorClass={statusColor(status)} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none p-1 -m-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-sm">
          <DetailRow label="Candidate">
            <div className="text-slate-800 font-medium">
              {interview.candidateName || '-'}
            </div>
            {interview.candidateEmail && (
              <a
                href={`mailto:${interview.candidateEmail}`}
                className="text-teal-600 hover:text-teal-700 text-xs"
              >
                {interview.candidateEmail}
              </a>
            )}
          </DetailRow>

          <DetailRow label="Host">
            <div className="text-slate-800">{interview.hostName || '-'}</div>
            {interview.hostEmail && (
              <a
                href={`mailto:${interview.hostEmail}`}
                className="text-teal-600 hover:text-teal-700 text-xs"
              >
                {interview.hostEmail}
              </a>
            )}
          </DetailRow>

          <DetailRow label="Scheduled">
            <div className="text-slate-800">{formatDateTime(interview.scheduledAt)}</div>
          </DetailRow>

          <DetailRow label="Duration">
            <div className="text-slate-800">
              {interview.durationMinutes != null ? `${interview.durationMinutes} minutes` : '-'}
            </div>
          </DetailRow>

          <DetailRow label="Invite link">
            <CopyButton value={inviteLink} />
          </DetailRow>
        </div>

        {/* Footer actions */}
        <div className="p-5 border-t border-slate-200 space-y-2">
          {status === 'SCHEDULED' && !interview.sessionId && (
            <>
              <button
                disabled={actionLoading}
                onClick={() => onStart(interview)}
                className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-500
                           text-white text-sm font-semibold rounded-lg
                           transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Starting...' : 'Start Interview'}
              </button>
              <button
                disabled={actionLoading}
                onClick={() => onCancel(interview)}
                className="w-full px-4 py-2.5 border border-red-300 text-red-600
                           hover:bg-red-50 text-sm font-semibold rounded-lg
                           transition-colors disabled:opacity-50"
              >
                Cancel Interview
              </button>
            </>
          )}

          {status === 'ACTIVE' && interview.sessionId && (
            <button
              disabled={actionLoading}
              onClick={() => onRejoin(interview)}
              className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-500
                         text-white text-sm font-semibold rounded-lg
                         transition-colors disabled:opacity-50"
            >
              Rejoin Session
            </button>
          )}

          {status === 'COMPLETED' && interview.sessionId && (
            <button
              onClick={() => onViewReport(interview)}
              className="w-full px-4 py-2.5 bg-slate-800 hover:bg-slate-700
                         text-white text-sm font-semibold rounded-lg transition-colors"
            >
              View Report
            </button>
          )}

          {status === 'CANCELLED' && (
            <p className="text-xs text-slate-500 text-center italic">
              This interview was cancelled.
            </p>
          )}
        </div>
      </aside>
    </>
  );
}

function DetailRow({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}
